/**
 * WebhookRulesList - Listagem de regras de webhook
 * 
 * Tabela estilo CRM com estatísticas inline, botões de ação
 * e visualização de endpoint.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Webhook, 
  Copy, 
  Check, 
  Play, 
  Pencil, 
  Trash2,
  Activity,
  Plus,
  CheckCircle2,
  XCircle,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';
import type { RoutingRuleWithWebhook } from './types';

// ============= INTERFACES =============

interface WebhookRulesListProps {
  /** Regras de webhook */
  rules: RoutingRuleWithWebhook[];
  /** Mapa de inboxes (id -> nome) */
  inboxesMap: Record<string, string>;
  /** Se está carregando */
  loading: boolean;
  /** Se pode criar novas regras */
  canCreate: boolean;
  /** Se pode editar */
  canEdit: boolean;
  /** Se pode deletar */
  canDelete: boolean;
  /** Callback para criar nova regra */
  onCreateRule: () => void;
  /** Callback para editar regra */
  onEditRule: (rule: RoutingRuleWithWebhook) => void;
  /** Callback para testar regra */
  onTestRule: (rule: RoutingRuleWithWebhook) => void;
  /** Callback para deletar regra */
  onDeleteRule: (rule: RoutingRuleWithWebhook) => void;
  /** Callback para toggle ativo */
  onToggleActive: (rule: RoutingRuleWithWebhook) => void;
}

// ============= COMPONENTE PRINCIPAL =============

export function WebhookRulesList({
  rules,
  inboxesMap,
  loading,
  canCreate,
  canEdit,
  canDelete,
  onCreateRule,
  onEditRule,
  onTestRule,
  onDeleteRule,
  onToggleActive,
}: WebhookRulesListProps) {
  // Estado para controlar qual URL foi copiada
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Construir URL do webhook usando helper centralizado (mesmo padrão do CRM)
  const getWebhookUrl = (token: string) => {
    const baseUrl = getEdgeFunctionUrl('routing-webhook');
    return `${baseUrl}?token=${token}`;
  };

  // Copiar URL para clipboard
  const copyUrl = async (rule: RoutingRuleWithWebhook) => {
    if (!rule.webhook_token) return;

    try {
      const url = getWebhookUrl(rule.webhook_token);
      await navigator.clipboard.writeText(url);
      setCopiedId(rule.id);
      toast.success('URL copiada para a área de transferência');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Erro ao copiar URL');
    }
  };

  // Calcular taxa de sucesso
  const getSuccessRate = (rule: RoutingRuleWithWebhook): number => {
    const total = rule.webhook_total_requests || 0;
    const success = rule.webhook_successful_requests || 0;
    if (total === 0) return 0;
    return Math.round((success / total) * 100);
  };

  // Obter nome das inboxes
  const getInboxNames = (inboxIds: string[] | null): string => {
    if (!inboxIds || inboxIds.length === 0) return 'Todas';
    const names = inboxIds
      .map(id => inboxesMap[id] || id.substring(0, 8))
      .slice(0, 2);
    const remaining = inboxIds.length - 2;
    return remaining > 0 ? `${names.join(', ')} +${remaining}` : names.join(', ');
  };

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="group relative overflow-hidden transition-all duration-300 hover:shadow-md">
      {/* Gradiente decorativo no hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-500/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      <CardHeader className="relative pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Ícone em container colorido (rosa para webhook) */}
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600">
              <Webhook className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Webhooks de Açoes rápidas</CardTitle>
              <CardDescription>
                {rules.length === 0
                  ? 'Nenhum webhook configurado'
                  : `${rules.length} webhook${rules.length > 1 ? 's' : ''} configurado${rules.length > 1 ? 's' : ''}`}
              </CardDescription>
            </div>
          </div>

          {/* Botão Nova Regra */}
          {canCreate && (
            <Button size="xs" onClick={onCreateRule}>
              <Plus className="mr-1.5" />
              Novo Webhook
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="relative pt-0">
        {rules.length === 0 ? (
          /* Estado vazio */
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mb-4">
              <Webhook className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-1">
              Nenhum webhook configurado
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Crie um webhook para receber gatilhos de sistemas externos como ClickSign, ERPs, CRMs, etc.
            </p>
            {canCreate && (
              <Button onClick={onCreateRule} variant="outline" size="xs" className="mt-4">
                <Plus className="mr-1.5" />
                Criar primeiro webhook
              </Button>
            )}
          </div>
        ) : (
          /* Tabela de webhooks */
          <div className="rounded-lg border bg-background/50 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableHead className="font-semibold">Nome</TableHead>
                  <TableHead className="w-24 text-center font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Inbox</TableHead>
                  <TableHead className="w-24 text-center font-semibold">Reqs</TableHead>
                  <TableHead className="w-20 text-center font-semibold">Taxa</TableHead>
                  <TableHead className="font-semibold">Endpoint</TableHead>
                  <TableHead className="w-32 text-right font-semibold">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((rule) => {
                  const successRate = getSuccessRate(rule);
                  const totalReqs = rule.webhook_total_requests || 0;

                  return (
                    <TableRow
                      key={rule.id}
                      className={`cursor-pointer transition-colors ${!rule.is_active ? 'opacity-50' : ''}`}
                      onClick={() => onEditRule(rule)}
                    >
                      {/* Nome */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Webhook className="h-4 w-4 text-rose-500" />
                          <div>
                            <p className="font-medium">{rule.name}</p>
                            {rule.description && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                {rule.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex justify-center">
                              <Switch
                                checked={rule.is_active}
                                onCheckedChange={() => onToggleActive(rule)}
                                disabled={!canEdit}
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {rule.is_active ? 'Desativar' : 'Ativar'}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>

                      {/* Inbox */}
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getInboxNames(rule.inbox_ids)}
                        </Badge>
                      </TableCell>

                      {/* Requisições */}
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-mono text-sm">{totalReqs}</span>
                        </div>
                      </TableCell>

                      {/* Taxa de Sucesso */}
                      <TableCell className="text-center">
                        <Badge
                          variant="secondary"
                          className={
                            successRate >= 80
                              ? 'bg-green-500/10 text-green-600 border-green-500/20'
                              : successRate >= 50
                              ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                              : 'bg-red-500/10 text-red-600 border-red-500/20'
                          }
                        >
                          {successRate}%
                        </Badge>
                      </TableCell>

                      {/* Endpoint */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded font-mono truncate max-w-[120px]">
                            ...{rule.webhook_token?.substring(0, 12)}
                          </code>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyUrl(rule);
                                }}
                              >
                                {copiedId === rule.id ? (
                                  <Check className="h-3.5 w-3.5 text-green-500" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {copiedId === rule.id ? 'Copiado!' : 'Copiar URL'}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>

                      {/* Ações */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-0.5">
                          {/* Testar */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onTestRule(rule)}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Testar webhook</TooltipContent>
                          </Tooltip>

                          {/* Editar */}
                          {canEdit && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => onEditRule(rule)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Editar webhook</TooltipContent>
                            </Tooltip>
                          )}

                          {/* Excluir */}
                          {canDelete && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => onDeleteRule(rule)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Excluir webhook</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
